/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Actionable, Button, CheckmarkCircle, Icon, TagIcon, Text, View } from '@az/starc-ui';
import { ExpirationDate } from '../ExpirationDate/ExpirationDate';
import { useModalOPPromptContext } from '@/components/ModalOPPromptContext';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { localStorage } from '@/utils/localStorage';
import { usePostAcknowldegeMutation } from '@/features/rewards/api/postRewardsAcknowledge';
import commonStyles from '../styles.module.scss';
import { PromoCode } from '../PromoCode/PromoCode';
import { useUpdatePreferencesV2 } from '@/features/myProfile/api/putUpdatePreferencesV2';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { OPTED_COMMUNICATION } from '../../constants/optIn';
import { addOptIn } from '../../utils/addOptIn';
import { trackOptIn } from '../../utils/trackOptIn';

const labelMap = {
  get: 'label_get',
  order: 'label_order',
  signupButton: 'label_email_signup_button',
  receiveExclusiveOffer: 'label_receive_exclusive_offer',
  validToSTH: 'label_valid_to_ship_to_home',
  seeDetails: 'label_see_details',
  youUnlocked: 'label_you_unlocked',
  off: 'label_off',
};

export const OptIn = () => {
  const labels = useLabels(labelMap);
  const { emailPromptAlertType, offer, promoState, updatePromoState } = useModalOPPromptContext();
  const { data: profile } = useMyProfile();
  const { mutate: acknowledgementMutation } = usePostAcknowldegeMutation();
  const { mutate: updatePreferences, isPending } = useUpdatePreferencesV2({
    onSuccess: () => {
      localStorage.setItem('displayEmailOptInAlert', 'false');
      updatePromoState({
        isOpen: true,
        isClosed: false,
        isConfirmed: true,
      });
    },
  });

  const { isConfirmed } = promoState;

  const handlePostRequest = () => acknowledgementMutation({ alertType: 'OP' });

  const handleConfirmSignup = () => {
    if (!profile) {
      return;
    }
    trackOptIn('optInPromoEmailOffers', offer?.discount);
    if (emailPromptAlertType) {
      handlePostRequest();
    }

    const optIns = { ...profile.optins };

    const emailOptins = addOptIn(optIns.emailOptins, OPTED_COMMUNICATION);

    updatePreferences({
      ...optIns,
      emailOptins,
    });
  };

  return (
    <View className={styles.emailPromoExpanded} aria-hidden={!promoState.isOpen}>
      <View className={styles.promoHeader}>
        <Icon
          size={{ s: 8, m: 10 }}
          svg={isConfirmed ? CheckmarkCircle : TagIcon}
          className={styles.promoIcon}
        />
        <Text variant="display-4" className={commonStyles.promoHeader}>
          {offer
            ? isConfirmed
              ? `${labels.youUnlocked} ${offer.value} ${labels.off}`
              : `${labels.get} ${offer.discountText}+ ${labels.order}`
            : isConfirmed
              ? labels.youUnlocked
              : `${labels.get} ${labels.order}`}
        </Text>
      </View>
      <View className={commonStyles.promoDetails}>
        {isConfirmed && offer ? (
          <PromoCode />
        ) : (
          <>
            <Button onClick={handleConfirmSignup} loading={isPending}>
              {labels.signupButton}
            </Button>
            <Text variant="subtitle-regular" className={styles.promoSubText}>
              {labels.receiveExclusiveOffer}
            </Text>
          </>
        )}
      </View>
      <View className={styles.promoFooter}>
        <ExpirationDate />
        <Text variant="subtitle-regular" className={styles.promoLegal}>
          {labels.validToSTH}{' '}
          <Actionable className={styles.promoSeeDetails} href="/lp/autozone-savings">
            {labels.seeDetails}
          </Actionable>
        </Text>
      </View>
    </View>
  );
};
